import { appData } from "./appData";
import { ContactInfoData, FooterLink } from "../types/interfaces";

export interface SocialSetting {
  id: string;
  label: string;
  url: string;
  description: string;
  showInFooter: boolean;
}

export interface ContactSettings {
  locationLabel: string;
  email: string;
  heroTitle: string;
  heroDescription: string;
  whatsappChannelUrl: string;
  whatsappCommunityUrl: string;
  socials: SocialSetting[];
}

const fallbackInfo = appData.pages.contact.sections.find(
  (section): section is ContactInfoData => section.type === "ContactInfo"
);

if (!fallbackInfo) {
  throw new Error("AlluraTech contact information is missing.");
}

const socialIds: Record<string, string> = {
  LinkedIn: "linkedin",
  X: "x",
  Telegram: "telegram",
  TikTok: "tiktok",
  "Founder LinkedIn": "founder-linkedin",
};

export const defaultContactSettings: ContactSettings = {
  locationLabel: fallbackInfo.address,
  email: fallbackInfo.email,
  heroTitle: "Let's make the next step clear.",
  heroDescription:
    "Whether you need a website or app, an immersive XR experience, or an AI-assisted creative workflow, tell us what you are building. We will help you choose a practical next step.",
  whatsappChannelUrl:
    fallbackInfo.socialLinks.find((link) => link.name === "WhatsApp Channel")?.href || "",
  whatsappCommunityUrl: fallbackInfo.whatsappCommunity,
  socials: fallbackInfo.socialLinks
    .filter((link) => Boolean(socialIds[link.name]))
    .map((link) => ({
      id: socialIds[link.name],
      label: link.label || link.name,
      url: link.href,
      description: link.description || "",
      showInFooter: !link.excludeFromFooter,
    })),
};

export const CONTACT_SETTINGS_API = (
  process.env.REACT_APP_ALLURAXR_API_URL ||
  "https://alluraxr-backend-grbfg2eydph9ekeg.swedencentral-01.azurewebsites.net"
).replace(/\/$/, "");

const isBrokenAlluraTechXLink = (social: SocialSetting): boolean => {
  if (social.id !== "x") return false;

  try {
    const url = new URL(social.url);
    return (
      ["x.com", "www.x.com", "twitter.com", "www.twitter.com"].includes(
        url.hostname.toLowerCase()
      ) && url.pathname.replace(/\/+$/, "").toLowerCase() === "/alluratechoy"
    );
  } catch {
    return false;
  }
};

export const contactSettingsToLinks = (settings: ContactSettings): FooterLink[] => {
  const externalSocials: FooterLink[] = settings.socials
    .filter((social) => Boolean(social.url) && !isBrokenAlluraTechXLink(social))
    .map((social) => ({
      name: ({
        linkedin: "LinkedIn",
        x: "X",
        telegram: "Telegram",
        tiktok: "TikTok",
        "founder-linkedin": "Founder LinkedIn",
      } as Record<string, string>)[social.id] || social.label,
      label: social.label,
      href: social.url,
      description: social.description,
      excludeFromFooter: !social.showInFooter,
    }));

  const whatsappLinks: FooterLink[] = [
    {
      name: "WhatsApp",
      label: "WhatsApp community",
      href: settings.whatsappCommunityUrl,
      description: "Join the AlluraTech community",
    },
    {
      name: "WhatsApp Channel",
      label: "WhatsApp Channel",
      href: settings.whatsappChannelUrl,
      description: "Follow official AlluraTech updates",
    },
  ].filter((link) => Boolean(link.href));

  const byName = new Map(externalSocials.map((link) => [link.name, link]));
  return [
    byName.get("LinkedIn"),
    byName.get("X"),
    ...whatsappLinks,
    byName.get("Telegram"),
    byName.get("TikTok"),
    byName.get("Founder LinkedIn"),
  ].filter((link): link is FooterLink => Boolean(link));
};

const isSafeHttpsUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      !url.username &&
      !url.password &&
      !["wa.me", "api.whatsapp.com"].includes(url.hostname.toLowerCase())
    );
  } catch {
    return false;
  }
};

export const isContactSettings = (value: unknown): value is ContactSettings => {
  if (!value || typeof value !== "object") return false;
  const data = value as Partial<ContactSettings>;
  return (
    typeof data.locationLabel === "string" &&
    typeof data.email === "string" &&
    typeof data.heroTitle === "string" &&
    typeof data.heroDescription === "string" &&
    typeof data.whatsappChannelUrl === "string" &&
    isSafeHttpsUrl(data.whatsappChannelUrl) &&
    new URL(data.whatsappChannelUrl).hostname === "whatsapp.com" &&
    typeof data.whatsappCommunityUrl === "string" &&
    isSafeHttpsUrl(data.whatsappCommunityUrl) &&
    new URL(data.whatsappCommunityUrl).hostname === "chat.whatsapp.com" &&
    Array.isArray(data.socials) &&
    data.socials.every(
      (social) =>
        typeof social.id === "string" &&
        typeof social.label === "string" &&
        typeof social.url === "string" &&
        isSafeHttpsUrl(social.url) &&
        typeof social.description === "string" &&
        typeof social.showInFooter === "boolean"
    )
  );
};
